/**
 * One laid-out day for the demo plan — the preview shape the timeline and
 * Live cards read — plus the lists the groups choose from. Sydney is UTC+11
 * in October; instants derive from the local minute (Danish echo = formatter).
 * For Media Devoted | Created By Ciro Autiero
 */
import type {
  DayPlanContainerRow,
  DayPlanDropRow,
  DayPlanGroupRow,
  DayPlanPreview,
} from '@trafficpulse/shared';

import { CLEARANCE_CLICKS, DEMO_RUN_DATE, SEGMENTS, WARM_UP } from './demo-plan';

const SYDNEY_OFFSET_MIN = 11 * 60;

/** The UTC instant of a recipient-local minute on the demo day. */
export const atUtc = (minute: number) =>
  new Date(Date.parse(`${DEMO_RUN_DATE}T00:00:00.000Z`) + (minute - SYDNEY_OFFSET_MIN) * 60_000).toISOString();

export interface DemoList {
  wiserId: number;
  name: string | null;
  leadCount: number | null;
}

/** The clickers group's lists, as the catalog holds them (unordered). */
export const CLICKERS_LISTS: DemoList[] = [
  { wiserId: 37125, name: 'AU_ACE_UCLICK_2026_NON-VOLUUM_CLICKERS', leadCount: 18_400 },
  { wiserId: 4206, name: 'AU_ABDELH_CLICKERS', leadCount: 41_200 },
  { wiserId: 38001, name: 'AU_AFFR_CLICKERS', leadCount: 9_800 },
  { wiserId: 38002, name: 'AU_AFFR_CLICKERS_SEPTEMBER', leadCount: 6_100 },
  { wiserId: 38003, name: 'AU_CLICKERS_LAST30', leadCount: 2_300 },
  { wiserId: 38004, name: 'AU_CLICKERS_OLD', leadCount: null },
];

export const FTD_LISTS: DemoList[] = [
  { wiserId: 28178, name: 'au_ftds_2025_1.5k', leadCount: 1_500 },
  { wiserId: 31312, name: 'AU_ACE_FTD_ALL TIME', leadCount: 1_200 },
];

const group = (
  listGroupId: string,
  groupName: string,
  groupColor: string,
  today: DemoList,
  next: DemoList | null,
): DayPlanGroupRow => ({
  listGroupId,
  groupName,
  groupColor,
  listOfDay: { wiserId: today.wiserId, name: today.name },
  skipped: [],
  next: next ? { wiserId: next.wiserId, name: next.name } : null,
  paused: null,
});

export const CLICKERS = group('g-clickers', 'clickers', '#6366f1', CLICKERS_LISTS[1], CLICKERS_LISTS[0]);
export const FTDS = group('g-ftds', 'FTDs', '#ec4899', FTD_LISTS[0], FTD_LISTS[1]);

const drops = (
  startMin: number,
  intervalMin: number,
  amounts: number[],
  g: DayPlanGroupRow,
): DayPlanDropRow[] =>
  amounts.map((amount, i) => ({
    slotId: `s${i + 1}`,
    minute: startMin + i * intervalMin,
    workDate: DEMO_RUN_DATE,
    atUtc: atUtc(startMin + i * intervalMin),
    groupId: g.listGroupId,
    listWiserId: g.listOfDay!.wiserId,
    listName: g.listOfDay!.name,
    amount,
    clamped: false,
    capacityUnknown: false,
    routeId: 1751,
    routeName: 'IT-Decision',
    routeIndicative: false,
    estCost: amount * 0.022,
    exceeds24h: false,
  }));

const row = (
  c: typeof WARM_UP,
  startMin: number,
  groups: DayPlanGroupRow[],
  list: DayPlanDropRow[],
): DayPlanContainerRow => ({
  containerId: c.id,
  name: c.name,
  color: c.color,
  startMin,
  groups,
  drops: list,
  totals: {
    drops: list.length,
    sms: list.reduce((s, d) => s + d.amount, 0),
    cost: list.reduce((s, d) => s + (d.estCost ?? 0), 0),
  },
});

const warmUpDrops = drops(11 * 60, 35, [500, 500, 500, 500, 500, 1000, 1000], CLICKERS);
const clearanceDrops = drops(14 * 60, 15, Array.from({ length: 29 }, () => 300), CLICKERS);
const segmentsStart = 21 * 60 + 10;
const segmentsDrops = drops(segmentsStart, 60, [800, 800, 900, 800, 800, 800], CLICKERS).map((d, i) =>
  i % 2 === 1 ? { ...d, groupId: FTDS.listGroupId, listWiserId: FTDS.listOfDay!.wiserId, listName: FTDS.listOfDay!.name } : d,
);

export const DEMO_DAY: DayPlanPreview = {
  runDate: DEMO_RUN_DATE,
  generateAtUtc: atUtc(8 * 60),
  window: { startMin: 9 * 60, endMin: 21 * 60, startUtc: atUtc(9 * 60), endUtc: atUtc(21 * 60) },
  containers: [
    row(WARM_UP, 11 * 60, [CLICKERS], warmUpDrops),
    row(CLEARANCE_CLICKS, 14 * 60, [CLICKERS], clearanceDrops),
    row(SEGMENTS, segmentsStart, [CLICKERS, FTDS], segmentsDrops),
  ],
  totals: { drops: 42, sms: 18_900, cost: null },
  aiCalls: { texts: 3, senders: 0 },
  sweepCount: 0,
  warnings: [],
};
